<!-- SPDX-License-Identifier: AGPL-3.0-only -->

<script setup lang="ts">
import { computed, ref, type ComputedRef, type Ref } from "vue";
import { useRoute, type RouteLocationNormalizedLoaded, type RouteLocationRaw } from "vue-router";
import type { AuthenticatedSession } from "../api/authentication.js";
import type { TenantSummary } from "../api/tenants.js";
import { useAuthenticationStore } from "../stores/authentication.js";
import { useTenantStore } from "../stores/tenants.js";
import NavigationIcon, { type NavigationIconName } from "./NavigationIcon.vue";
import { canManageSystemUsers, canViewSystem } from "../api/system-authorization.js";

interface NavigationItem {
  readonly label: string;
  readonly icon: NavigationIconName;
  readonly routeName: string;
  readonly routeParameters?: Readonly<Record<string, string>>;
  readonly activeRouteNames?: readonly string[];
}

const SIDEBAR_STORAGE_KEY: string = "lacunascope:sidebar-collapsed";
const authenticationStore: ReturnType<typeof useAuthenticationStore> = useAuthenticationStore();
const tenantStore: ReturnType<typeof useTenantStore> = useTenantStore();
const route: RouteLocationNormalizedLoaded = useRoute();
const collapsed: Ref<boolean> = ref(localStorage.getItem(SIDEBAR_STORAGE_KEY) === "true");
const session: ComputedRef<AuthenticatedSession | null> = computed(
  (): AuthenticatedSession | null => authenticationStore.session,
);
const selectedTenant: ComputedRef<TenantSummary | null> = computed(
  (): TenantSummary | null => tenantStore.selectedTenant,
);
const hasSystemAccess: ComputedRef<boolean> = computed(
  (): boolean => canViewSystem(session.value?.user.systemRoles ?? []),
);
const systemItems: ComputedRef<readonly NavigationItem[]> = computed(
  (): readonly NavigationItem[] => {
    const items: NavigationItem[] = [
      { label: "Dashboard", icon: "dashboard", routeName: "system-dashboard" },
      { label: "Tenants", icon: "tenants", routeName: "system-tenants" },
    ];
    if (canManageSystemUsers(session.value?.user.systemRoles ?? [])) {
      items.push(
        { label: "Users", icon: "users", routeName: "system-users" },
        { label: "Tiers", icon: "tiers", routeName: "system-tiers" },
        { label: "Firmware", icon: "probe", routeName: "system-firmware" },
        { label: "Notifications", icon: "notifications", routeName: "system-notifications" },
        { label: "Settings", icon: "settings", routeName: "system-settings" },
      );
    }
    return items;
  },
);
const tenantItems: ComputedRef<readonly NavigationItem[]> = computed(
  (): readonly NavigationItem[] => {
    const tenant: TenantSummary | null = selectedTenant.value;
    if (tenant === null) {
      return [];
    }
    const items: NavigationItem[] = [];
    if (tenant.access.capabilities.includes("measurement.read")) {
      items.push({
        label: "Dashboard",
        icon: "dashboard",
        routeName: "tenant-dashboard",
        routeParameters: { tenantId: tenant.id },
      });
    }
    items.push({
      label: "Overview",
      icon: "overview",
      routeName: "tenant-overview",
      routeParameters: { tenantId: tenant.id },
    });
    if (tenant.access.capabilities.includes("probe.read")) {
      items.push({
        label: "Probes",
        icon: "probe",
        routeName: "tenant-probes",
        routeParameters: { tenantId: tenant.id },
        activeRouteNames: ["tenant-probes", "tenant-probe-rollout", "tenant-probe-configuration"],
      });
    }
    if (tenant.access.capabilities.includes("tenant.members.manage")) {
      items.push({
        label: "Members",
        icon: "members",
        routeName: "tenant-members",
        routeParameters: { tenantId: tenant.id },
      });
    }
    if (tenant.access.capabilities.includes("tenant.notifications.read")) {
      items.push({
        label: "Notifications",
        icon: "notifications",
        routeName: "tenant-notifications",
        routeParameters: { tenantId: tenant.id },
      });
    }
    return items;
  },
);
const accountItems: readonly NavigationItem[] = [
  { label: "User settings", icon: "account", routeName: "user-settings" },
];

function toggleCollapsed(): void {
  collapsed.value = !collapsed.value;
  localStorage.setItem(SIDEBAR_STORAGE_KEY, String(collapsed.value));
}

function navigationTarget(item: NavigationItem): RouteLocationRaw {
  return item.routeParameters === undefined
    ? { name: item.routeName }
    : { name: item.routeName, params: item.routeParameters };
}

function navigationActive(item: NavigationItem): boolean {
  const activeRouteNames: readonly string[] = item.activeRouteNames ?? [item.routeName];
  return typeof route.name === "string" && activeRouteNames.includes(route.name);
}
</script>

<template>
  <aside class="app-sidebar" :class="{ 'app-sidebar--collapsed': collapsed }">
    <nav class="app-navigation" aria-label="Application navigation">
      <section v-if="hasSystemAccess" class="navigation-section">
        <p class="navigation-section__label">System</p>
        <RouterLink
          v-for="item in systemItems"
          :key="item.routeName"
          class="navigation-link"
          :class="{ 'navigation-link--active': navigationActive(item) }"
          :to="navigationTarget(item)"
          :title="collapsed ? item.label : undefined"
        >
          <NavigationIcon :name="item.icon" />
          <span>{{ item.label }}</span>
        </RouterLink>
      </section>

      <section v-if="tenantItems.length > 0" class="navigation-section">
        <p class="navigation-section__label">Tenant</p>
        <RouterLink
          v-for="item in tenantItems"
          :key="item.routeName"
          class="navigation-link"
          :class="{ 'navigation-link--active': navigationActive(item) }"
          :to="navigationTarget(item)"
          :title="collapsed ? item.label : undefined"
        >
          <NavigationIcon :name="item.icon" />
          <span>{{ item.label }}</span>
        </RouterLink>
      </section>

      <section class="navigation-section">
        <p class="navigation-section__label">Account</p>
        <RouterLink
          v-for="item in accountItems"
          :key="item.routeName"
          class="navigation-link"
          :class="{ 'navigation-link--active': navigationActive(item) }"
          :to="navigationTarget(item)"
          :title="collapsed ? item.label : undefined"
        >
          <NavigationIcon :name="item.icon" />
          <span>{{ item.label }}</span>
        </RouterLink>
      </section>
    </nav>

    <button
      class="sidebar-toggle"
      type="button"
      :aria-label="collapsed ? 'Expand navigation' : 'Collapse navigation'"
      :title="collapsed ? 'Expand navigation' : 'Collapse navigation'"
      @click="toggleCollapsed"
    >
      <NavigationIcon name="collapse" />
      <span>Collapse</span>
    </button>
  </aside>
</template>
